import { useMemo, useEffect } from "react"
import { useAppDispatch } from "../../reduxStore/hooks"
import {
  useCMSCollectionsLoaded,
  useCMSCollectionsLoading,
  useCMSCollectionsPending,
  useCMSCollectionErrors,
} from "./useCMSCollectionStatus"
import { Content } from "./schema"
import { useSmartCMSContent } from "./useSmartCMSContent"

export interface CMSReadyResult {
  cms: Content
  isLoading: boolean
  isReady: boolean
  hasError: boolean
}

export const useCMSReady = (collections: (keyof Content)[]): CMSReadyResult => {
  const dispatch = useAppDispatch()
  const cms = useSmartCMSContent()

  // Always call these hooks to respect Rules of Hooks
  const areCMSCollectionsLoaded = useCMSCollectionsLoaded(collections)
  const areCMSCollectionsLoading = useCMSCollectionsLoading(collections)
  const areCMSCollectionsPending = useCMSCollectionsPending(collections)
  const hasCMSLoadingError = useCMSCollectionErrors(collections)

  // Auto-load pending collections in lazy mode
  useEffect(() => {
    if (areCMSCollectionsPending) {
      const collectionsToLoad = collections.filter((collection) => {
        const isLoaded = cms[collection] !== undefined
        return !isLoaded
      })
      // Avoid dispatching if no collections need loading
      if (collectionsToLoad.length === 0) {
        return
      }
      // Use setTimeout to avoid dispatching during render
      setTimeout(() => {
        import("./lazyCollectionLoader").then(({ loadCMSCollections }) => {
          dispatch(loadCMSCollections(collectionsToLoad))
        })
      }, 0)
    }
  }, [dispatch, collections, areCMSCollectionsPending, cms])

  return useMemo(() => {
    //use collection status hooks
    const isLoading = areCMSCollectionsLoading || areCMSCollectionsPending
    const isCMSContentReady =
      areCMSCollectionsLoaded && !isLoading && !hasCMSLoadingError

    return {
      cms,
      isLoading,
      isReady: isCMSContentReady,
      hasError: hasCMSLoadingError,
    }
  }, [
    cms,
    areCMSCollectionsLoaded,
    areCMSCollectionsLoading,
    areCMSCollectionsPending,
    hasCMSLoadingError,
  ])
}
